import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Box, TextField, Typography, Grid, FormControl, InputLabel, Select, MenuItem, Divider, Chip, InputAdornment, SelectChangeEvent } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { HotelFormData } from '@types';

// --- Constants (Can be imported) ---
const legalEntityTypes = ['Corporation', 'LLC', 'Partnership', 'LP', 'Sole Proprietorship', 'Trust', 'REIT', 'Joint Venture', 'Non-Profit', 'Other'];
const ownershipStructures = [
  'Single Owner',
  'Joint Ownership',
  'Partnership',
  'Investment Group',
  'Family Trust',
  'Corporate Entity',
  'REIT',
  'Private Equity',
  'Institutional Investor',
  'Foreign Investor',
  'Other',
];

const OwnerDetailsStep: React.FC = () => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<HotelFormData>();

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" gutterBottom>
        Owner Information
      </Typography>
      <Grid container spacing={2}>
        {/* Basic Owner Info */}
        <Grid item xs={12} sm={6}>
          <TextField label="Owner Name" fullWidth {...register('owner_name')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Owner Entity/Company" fullWidth {...register('owner_entity')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          {/* Controller for MUI Select */}
          <Controller
            name="legal_entity_type"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Legal Entity Type</InputLabel>
                <Select {...field} label="Legal Entity Type">
                  {legalEntityTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Tax ID" fullWidth {...register('tax_id')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Owner Type" fullWidth placeholder="Individual, Company, etc." {...register('owner_type')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="ownership_structure"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Ownership Structure</InputLabel>
                <Select {...field} label="Ownership Structure">
                  {ownershipStructures.map((structure) => (
                    <MenuItem key={structure} value={structure}>
                      {structure}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Ownership Percentage"
            type="number"
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment>, inputProps: { min: 0, max: 100, step: 0.01 } }}
            {...register('ownership_percentage', {
              valueAsNumber: false, // Keep as string for input, parse on submit
              min: { value: 0, message: 'Must be >= 0' },
              max: { value: 100, message: 'Must be <= 100' },
            })}
            error={!!errors.ownership_percentage}
            helperText={errors.ownership_percentage?.message?.toString()}
          />
        </Grid>
        {/* Dates using Controller */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="owner_since"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Owner Since"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(date) => field.onChange(date)} // RHF Controller handles the value format
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="acquisition_date"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker label="Acquisition Date" value={field.value ? dayjs(field.value) : null} onChange={(date) => field.onChange(date)} slotProps={{ textField: { fullWidth: true } }} />
              </LocalizationProvider>
            )}
          />
        </Grid>

        {/* Contact Info */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }}>
            <Chip label="Contact Information" />
          </Divider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Contact Person" fullWidth {...register('owner_contact_name')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Contact Email"
            type="email"
            fullWidth
            {...register('owner_contact_email', {
              pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email format' },
            })}
            error={!!errors.owner_contact_email}
            helperText={errors.owner_contact_email?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Contact Phone" fullWidth {...register('owner_contact_phone')} />
        </Grid>

        {/* Owner Address */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }}>
            <Chip label="Owner Address" />
          </Divider>
        </Grid>
        <Grid item xs={12}>
          <TextField label="Owner Address" fullWidth {...register('owner_address')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Owner City" fullWidth {...register('owner_city')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Owner State/Province" fullWidth {...register('owner_state')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Owner ZIP/Postal Code" fullWidth {...register('owner_zip')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Owner Country" fullWidth defaultValue="USA" {...register('owner_country')} />
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }}>
            <Chip label="Notes" />
          </Divider>
        </Grid>
        <Grid item xs={12}>
          <TextField label="Ownership Type Details" fullWidth multiline rows={3} placeholder="Additional details..." {...register('ownership_type_details')} />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Ownership Notes" fullWidth multiline rows={3} {...register('ownership_notes')} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default OwnerDetailsStep;
